import React, { useState } from 'react';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { BatchedChanges, ExternalAgreement, ExternalAgreementStatus } from '@/types';
import { useDefaultScrollToId, useAsyncState, useMergeState, useEffectSkipFirst } from '@/utils/hooks';
import { ProjectPermissionToggle } from '@/components/permissions';
import { Trans } from '@lingui/react/macro';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { searchExternalAgreements, SearchExternalAgreementsOptions } from '@/services/external-agreements';
import ExternalAgreementList from '@/seller/components/external-agreements/ExternalAgreementList';
import CreateExternalAgreementDialog from '@/seller/components/external-agreements/CreateExternalAgreementDialog';
import { Button, ButtonBar, SlideToggle } from '@ygtechnologies/rubus-ui';

interface ResidentialUnitChangeRequestsProps {
    residentialUnitId: number;
}

const ResidentialUnitExternalAgreements: React.FC<ResidentialUnitChangeRequestsProps> = ({ residentialUnitId }) => {
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [includeCancelled, setIncludeCancelled] = useState(false);
    const [filters, mergeFilters] = useMergeState<SearchExternalAgreementsOptions>({
        residentialUnitIds: [residentialUnitId],
    });

    useEffectSkipFirst(() => {
        setExternalAgreements(null);
        mergeFilters({ residentialUnitIds: [residentialUnitId] });
    }, [residentialUnitId, mergeFilters]);

    const [externalAgreements, setExternalAgreements, reloadExternalAgreements] = useAsyncState<ExternalAgreement[] | null>(
        () =>
            searchExternalAgreements({
                ...filters,
                statuses: includeCancelled
                    ? undefined
                    : [ExternalAgreementStatus.Approved, ExternalAgreementStatus.Pending, ExternalAgreementStatus.Rejected],
            }),
        [filters, includeCancelled],
        null
    );

    const defaultExpandedId = useDefaultScrollToId();

    const handleChange = (changes: BatchedChanges<ExternalAgreement>) => {
        if (!externalAgreements) {
            return;
        }
        setExternalAgreements(changes.updateArray(externalAgreements));
    };

    const handleAdd = () => {
        reloadExternalAgreements();
    };

    return (
        <div>
            <ButtonBar sticky margins="none">
                <ButtonBar.Right>
                    <SlideToggle onChange={(e) => setIncludeCancelled(!includeCancelled)} checked={includeCancelled}>
                        <Trans>Show cancelled agreements</Trans>
                    </SlideToggle>
                    <ProjectPermissionToggle condition="canUploadDocuments">
                        <Button variant="tertiary" onClick={() => setShowCreateDialog(true)} icon={faPlus}>
                            <Trans>Upload external agreement</Trans>
                        </Button>
                    </ProjectPermissionToggle>
                </ButtonBar.Right>
            </ButtonBar>

            {externalAgreements ? (
                <ExternalAgreementList
                    showUnit={false}
                    defaultExpandedId={defaultExpandedId}
                    externalAgreements={externalAgreements}
                    onChange={handleChange}
                />
            ) : (
                <LoadingIndicator />
            )}

            <CreateExternalAgreementDialog
                show={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                onSave={handleAdd}
                residentialUnitId={residentialUnitId}
            />
        </div>
    );
};

export default ResidentialUnitExternalAgreements;
